import {useLazyQuery} from '@apollo/client';
import {useCallback} from 'react';
import {PopReviewFilters, PopReviewTabType, PopReviewType, tabToIsFinished} from '../graphql/types/PopReviewType';
import {
    GET_POP_REVIEW_PAGE_LIST,
    GetPopReviewPageListInput,
    GetPopReviewPageListResponse,
    PopReviewFiltersTypeInput
} from '../graphql/queries/getPopReviewPageList';

export interface PopReviewQueryResult {
    popReviewData: PopReviewType[];
    loading: boolean;
    executeQuery: (filters: PopReviewFilters, page: number, size: number, tab: PopReviewTabType) => void;
    totalElements: number;
    error?: any;
}

/**
 * 构建筛选条件入参（列表查询与计数查询共用）
 * 全部条件为空时返回 undefined，省一次无意义的空对象传输；tab 由页面单独持有并在此合入。
 */
export const buildFiltersInputFromFilters = (
    filters: PopReviewFilters,
    tab?: PopReviewTabType
): PopReviewFiltersTypeInput | undefined => {
    const hasAnyCondition =
        !!filters.statuses?.length ||
        !!filters.appointmentNumbers ||
        !!filters.pickupHubId ||
        !!filters.pickupSiteId ||
        !!filters.driverId ||
        !!filters.disqualifyReasonCodes?.length ||
        !!filters.submittedAt?.[0] ||
        !!filters.submittedAt?.[1] ||
        !!filters.reviewedAt?.[0] ||
        !!filters.reviewedAt?.[1];

    if (!hasAnyCondition && !tab) {
        return undefined;
    }

    return {
        isFinished: tabToIsFinished(tab),
        statuses: filters.statuses?.length ? filters.statuses : undefined,
        appointmentNumbers: filters.appointmentNumbers || undefined,
        pickupHubId: filters.pickupHubId || undefined,
        pickupSiteId: filters.pickupSiteId || undefined,
        driverId: filters.driverId || undefined,
        disqualifyReasonCodes: filters.disqualifyReasonCodes?.length
            ? filters.disqualifyReasonCodes
            : undefined,
        // 后端为 `>= from` / `< to`（不含）；控件只选到日期，故规整到当日首尾时刻
        submittedAtFrom: filters.submittedAt?.[0]?.startOf('day').toISOString() ?? undefined,
        submittedAtTo: filters.submittedAt?.[1]?.endOf('day').toISOString() ?? undefined,
        reviewedAtFrom: filters.reviewedAt?.[0]?.startOf('day').toISOString() ?? undefined,
        reviewedAtTo: filters.reviewedAt?.[1]?.endOf('day').toISOString() ?? undefined
    };
};

const buildQueryVariables = (
    filters: PopReviewFilters,
    page: number,
    size: number,
    tab: PopReviewTabType
): GetPopReviewPageListInput => ({
    filters: buildFiltersInputFromFilters(filters, tab),
    page,
    size
});

export const usePopReviewQuery = (): PopReviewQueryResult => {
    const [execute, {data, loading, error}] = useLazyQuery<GetPopReviewPageListResponse>(
        GET_POP_REVIEW_PAGE_LIST,
        {
            fetchPolicy: 'network-only',
            nextFetchPolicy: 'network-only',
            errorPolicy: 'all',
            notifyOnNetworkStatusChange: true
        }
    );

    const executeQuery = useCallback(
        (filters: PopReviewFilters, page: number, size: number, tab: PopReviewTabType) => {
            execute({variables: buildQueryVariables(filters, page, size, tab)});
        },
        [execute]
    );

    return {
        popReviewData: data?.getPopReviewPageList?.content || [],
        loading,
        executeQuery,
        totalElements: data?.getPopReviewPageList?.totalElements || 0,
        error
    };
};
